/**
 * 이상한 방 - 봉투 화면의 버튼 줄([한 장 더] [열 장] [닫기]) (순수 DOM)
 *
 * 근거 문서: 로드맵 확정안 3.3절(1800ms - 버튼 · "핀이 모자라면 [한 장 더]가 'N판이면 다시 연다'로 이유를 말한다")
 *           GLOSSARY 3절(눌리지 않는 버튼은 이유를 말한다)
 *
 * `ui/envelope.ts`에서 갈라져 나왔다. 이 줄은 연출이 **끝난 뒤**에만 보인다(`showDone`) - 그 전에는 탭이 곧 건너뛰기라
 * 버튼이 있으면 탭과 버튼이 경쟁한다. 열 수 있는가(`view.next`)는 밖에서 계산돼 온다 - 핀 잔고와 값을 여기서 비교하면
 * 결제 규칙이 두 곳에 생긴다. 버튼에 적히는 값도 표(`price`)에서 온다.
 *
 * 다시 보기(`replay`)에는 [한 장 더]·[열 장]이 없다 - 저장이 움직이지 않는 화면에서 여는 버튼은 거짓 약속이다.
 */

import { t } from '../i18n'

import { createElement } from './dom'

import './envelope.css'

/* --- 타입 ---------------------------------------------------------------------- */

export type EnvelopeActionsHandlers = Readonly<{
  onOpen: (count: 1 | 10) => void
  onClose: () => void
}>

/** 끝난 뒤 버튼 줄이 알아야 하는 것. `EnvelopeView.next`와 `replay`가 그대로 온다. */
export type EnvelopeActionsState = Readonly<{
  next: Readonly<{ single: boolean; ten: boolean; roundsToAfford: number }>
  replay: boolean
}>

export type EnvelopeActions = Readonly<{
  /** `.ev-actions`. 루트에 붙이는 것은 화면의 몫이다. */
  root: HTMLElement
  /** 버튼 글자·값·잠긴 이유. 언어 전환도 이 함수를 다시 부른다. */
  /**
   * `open`이 `null`이면(아직 뷰가 없다) [닫기]만 칠한다 - 갈라지기 전에도 닫기 문구는 뷰 유무와 무관하게 칠해졌고, 그 순서를 지킨다.
   */
  paintTexts: (open: Readonly<{ price: Readonly<{ single: number; ten: number }>; roundsToAfford: number }> | null) => void
  /** 연출이 시작될 때. 줄을 감춘다. */
  hide: () => void
  /** 연출이 끝났다. 줄을 내고, 잠긴 버튼은 이유를 말하고, 누를 수 있는 가장 큰 버튼에 포커스를 준다. */
  showDone: (state: EnvelopeActionsState) => void
  /** 끝난 뒤의 Enter가 물을 것 - 큰 버튼이 살아 있는가. */
  canOpenOne: () => boolean
  dispose: () => void
}>

/* --- 본체 ---------------------------------------------------------------------- */

/** 버튼 줄을 만든다. 처음에는 감춰져 있다. */
export function createEnvelopeActions(handlers: EnvelopeActionsHandlers): EnvelopeActions {
  const actions = createElement('div', 'ev-actions')
  actions.hidden = true
  const oneMore = createElement('button', 'ev-primary', '')
  oneMore.type = 'button'
  const oneMoreWhy = createElement('p', 'ev-why', '')
  oneMoreWhy.hidden = true
  const tenMore = createElement('button', 'ev-secondary', '')
  tenMore.type = 'button'
  const close = createElement('button', 'ev-secondary', '')
  close.type = 'button'
  actions.append(oneMore, oneMoreWhy, tenMore, close)

  const runOneMore = (): void => {
    if (!oneMore.disabled) handlers.onOpen(1)
  }
  const runTenMore = (): void => {
    if (!tenMore.disabled) handlers.onOpen(10)
  }
  const runClose = (): void => handlers.onClose()

  oneMore.addEventListener('click', runOneMore)
  tenMore.addEventListener('click', runTenMore)
  close.addEventListener('click', runClose)

  return {
    root: actions,

    paintTexts(open): void {
      close.textContent = t('common.close')
      if (open === null) return
      const { price, roundsToAfford } = open
      oneMore.textContent = `${t('envelope.oneMore')} ${t('envelope.price', { price: price.single })}`
      tenMore.textContent = `${t('envelope.tenMore')} ${t('envelope.price', { price: price.ten })}`
      oneMoreWhy.textContent = t('envelope.notEnough', { n: roundsToAfford })
    },

    hide(): void {
      actions.hidden = true
    },

    showDone(state: EnvelopeActionsState): void {
      actions.hidden = false
      oneMore.hidden = state.replay
      tenMore.hidden = state.replay
      oneMore.disabled = !state.next.single
      tenMore.disabled = !state.next.ten
      // 눌리지 않는 버튼은 이유를 말한다(GLOSSARY 3절). 열 장은 한 장이 열리면 값만 다른 같은 문장이라 따로 말하지 않는다.
      oneMoreWhy.hidden = state.replay || state.next.single
      ;(state.replay || !state.next.single ? close : oneMore).focus()
    },

    canOpenOne: () => !oneMore.hidden && !oneMore.disabled,

    dispose(): void {
      oneMore.removeEventListener('click', runOneMore)
      tenMore.removeEventListener('click', runTenMore)
      close.removeEventListener('click', runClose)
    },
  }
}
